/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable unused-imports/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import Button, { Color } from "Components/Form/Button";
import { Banner } from "Components/Generic/Banner";
import { useTranslation } from "react-i18next";
import { AiOutlineCheck, AiOutlineClose } from "react-icons/ai";
import { FiInfo } from "react-icons/fi";
import * as yup from "yup";

import AutoSuggest from "../../../../../../../../../components/Form/AutoSuggest";
import Input from "../../../../../../../../../components/Form/Input";
import { useAppState } from "../../../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { Carrier } from "../../../../../../../../../models/carrier";
import { Commodity } from "../../../../../../../../../models/commodity";
import { Option } from "../../../../../../../../../models/option";
import CarrierService from "../../../../../../../../../services/carrier.service";
import {
  CarrierTdType,
  CarrierType,
  ShipmentType,
  Status,
} from "../../../../../../../../../util/enums";
import { mapBannerTypeByTrackingShipmentStatus } from "../../../../../utils/Waybills.util";
import { useShipmentTrackingHook } from "../../../../hooks/shipmentTrackingHook";
import { useShipmentContext } from "../../../../hooks/useContext";
import { ViewField } from "../../ViewField";
import useStyles, {
  BodyItem,
  BodyText,
  InfoCard,
  InfoCardBody,
  InfoCardColumn,
  Text,
} from "./styles";

import _ from "lodash";
import { SectionTitle } from "./section-title";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";

interface ServiceSectionProps {
  isOnView?: boolean;
  errors?: yup.ValidationError[];
}

export function ServiceSection({ isOnView = false, errors = [] }: ServiceSectionProps) {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { shipment, setShipment, commodities } = useShipmentContext();
  const isShipmentTrackingFetched = useMemo(() => shipment?.fetchedData, [shipment]);

  const isShowFetchDetails = useMemo(() => {
    return (
      (currentUser?.company?.airTrackingEnabled && shipment?.shipmentType === ShipmentType.AIR) ||
      (currentUser?.company?.seaTrackingEnabled && shipment?.shipmentType === ShipmentType.SEA)
    );
  }, [currentUser]);
  const [showTrackingStatusBanner, setShowTrackingStatusBanner] = useState(false);
  const [bannerInfo, setBannerInfo] = useState<any>({});
  const [{ getFetchingDetailsShipmentTrackingById }] = useShipmentTrackingHook();
  const styles = useStyles();

  const {
    data: shipmentTracking,
    status: shipmentTrackingStatus,
    isFetching: isShipmentTrackingFetching,
    refetch: getShipmentTrackingByIdRefetch,
  } = getFetchingDetailsShipmentTrackingById(
    shipment?.billOfLanding ?? "",
    shipment?.shipmentType,
    {
      enabled: false,
      select: (response) => {
        return response?.data || {};
      },
    }
  );

  const [showInfoCard, setShowInfoCard] = useState<boolean>(false);
  const hasMarineTraffic = useMemo(() => !!shipment?.marineTrafficId, [shipment]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { data: carrierList, isSuccess } = useQuery({
    queryKey: ["carrier.list"],
    queryFn: async () => {
      const response = await CarrierService.getAll({
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
        type: CarrierType.SEA,
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "trackableBy" }]),
      });

      if (response && response.data) return response.data;

      return [] as Carrier[];
    },
  });

  const onGetCommodityInputValue = () => {
    if (shipment?.commodity) {
      const commoditiesValue = shipment.commodity.split("/");
      const commoditiesArr = commodities?.filter((option) =>
        commoditiesValue.includes(option.value)
      );

      return commoditiesArr;
    }

    return [];
  };

  useEffect(() => {
    if (!isShipmentTrackingFetching) {
      setBannerInfo(
        mapBannerTypeByTrackingShipmentStatus(shipmentTrackingStatus, shipmentTracking)
      );
    }
    if (!_.isEmpty(shipmentTracking) && shipmentTrackingStatus === "success") {
      const updatedShipment = {
        ...shipment,
        fetchedData: true,
        vesselName: shipmentTracking.vesselName || shipment?.vesselName,
        voyageNumber: shipmentTracking.carrier || shipment?.voyageNumber,
        shipmentTracking: {
          ...shipment?.shipmentTracking,
          finalPortActualArrival:
            shipmentTracking?.finalPortActualArrivalUtc ||
            shipment?.shipmentTracking?.finalPortActualArrival,
          originPortActualDeparture:
            shipmentTracking.originPortActualDepartureUtc ||
            shipment?.shipmentTracking?.originPortActualDeparture,
          originPort: shipmentTracking.originPortName,
          finalPort: shipmentTracking.finalPortName,
        },
      };
      setShipment(updatedShipment);
    }
  }, [shipmentTracking, shipmentTrackingStatus, isShipmentTrackingFetching]);

  useEffect(() => {
    if (!_.isEmpty(shipment?.id)) {
      getShipmentTrackingByIdRefetch();
    }
  }, []);

  useEffect(() => {
    setShowTrackingStatusBanner(false);
  }, [shipment?.billOfLanding]);

  const onSetCarrierScacCodeByValue = (value: string) => {
    if (value) {
      const possibleScacCode = value?.substring(0, 4);
      const isCarrierExists = carrierList?.find((carrier) =>
        carrier.scac.toLowerCase().includes(possibleScacCode.toLowerCase())
      );

      if (isCarrierExists) {
        setShipment({ ...shipment, carrierScac: isCarrierExists?.scac });
        setShowInfoCard(true);
      }
    }
  };

  const renderStatusIcon = (trackableBy?: string) => {
    const carrier = carrierList?.find((carrier) => carrier.scac === shipment?.carrierScac);
    const findTrackableBy = carrier?.trackableBy?.find((tby) => tby.code === trackableBy);

    if (findTrackableBy) return <AiOutlineCheck size={18} color="#039855" />;

    return <AiOutlineClose size={18} color="#D92D20" />;
  };

  const renderCarrierViewField = (carrierScac?: string) => {
    if (!carrierList?.length || !carrierScac?.trim().length) return "";

    return `${
      carrierList?.find((carrier) => carrier.scac.toLowerCase() === carrierScac?.toLowerCase())
        ?.name
    } - ${carrierScac}`;
  };

  const renderCarrierValue = useMemo(() => {
    if (isSuccess && shipment?.carrierScac && carrierList?.length) {
      const carrier = carrierList?.find(
        (carrier) => carrier.scac.toLowerCase() === shipment?.carrierScac?.toLowerCase()
      );

      return carrier;
    }

    return undefined;
  }, [isSuccess, carrierList, shipment?.carrierScac]);

  const renderTrackingStatusBanner = useCallback(() => {
    return (
      showTrackingStatusBanner && (
        <Grid item xs={12}>
          <Banner
            type={bannerInfo?.type}
            onClose={() => {
              setBannerInfo({});
              setShowTrackingStatusBanner(false);
            }}
            label={bannerInfo?.label}
            prefix={bannerInfo?.prefix}
          />
        </Grid>
      )
    );
  }, [showTrackingStatusBanner, bannerInfo]);

  const disabledToFetchAndEditBookingOrBillOfLandingDetails = useMemo(() => {
    return (
      !!shipment?.id &&
      (isShipmentTrackingFetching || hasMarineTraffic || isShipmentTrackingFetched)
    );
  }, [shipment, isShipmentTrackingFetching, hasMarineTraffic, isShipmentTrackingFetched]);

  return (
    <Grid container item direction="column" spacing={3}>
      <Grid item xs={12}>
        <SectionTitle title={translate("service")} />
      </Grid>
      {isOnView ? (
        <Grid item container spacing={3} direction="row">
          <Grid item xs={12} md={6} lg={6}>
            <ViewField label={translate("billOfLanding")} value={shipment?.billOfLanding} />
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <ViewField label={`${translate("booking")} #`} value={shipment?.bookingNo} />
          </Grid>
        </Grid>
      ) : (
        <Grid container item spacing={3} direction="column">
          <Grid container item direction="row" spacing={3}>
            <Grid
              item
              xs={12}
              md={3}
              lg={3}
              style={{
                paddingBottom: 0,
              }}
            >
              <Input
                label={translate("billOfLanding")}
                placeholder={translate("enterBillOfLanding")}
                // required
                disabled={disabledToFetchAndEditBookingOrBillOfLandingDetails}
                value={shipment?.billOfLanding ?? ""}
                onChange={(event) => {
                  setShipment({ ...shipment, billOfLanding: event.target.value });
                }}
                onBlur={(event) => {
                  if (!shipment?.carrierScac?.trim().length)
                    onSetCarrierScacCodeByValue(event.target.value);
                }}
                error={fieldHasError("trackingAvailable")}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              lg={3}
              style={{
                paddingBottom: 0,
              }}
            >
              <Input
                label={`${translate("booking")} #`}
                placeholder={`${translate("enterBooking")} #`}
                disabled={disabledToFetchAndEditBookingOrBillOfLandingDetails}
                value={shipment?.bookingNo ?? ""}
                onChange={(event) => {
                  setShipment({ ...shipment, bookingNo: event.target.value });
                }}
                error={fieldHasError("trackingAvailable")}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              lg={3}
              style={{
                paddingBottom: 0,
              }}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "end",
                paddingBottom: 0,
              }}
            >
              {!isOnView && isShowFetchDetails && (
                <Button
                  onClick={() => {
                    if (shipment?.billOfLanding) {
                      setBannerInfo(mapBannerTypeByTrackingShipmentStatus("loading", {}));
                      setShowTrackingStatusBanner(true);
                      getShipmentTrackingByIdRefetch();
                    } else {
                      NotificationToast.error(
                        translate("trackYourShipmentByFillingInEitherOrBothFields")
                      );
                    }
                  }}
                  customColor={Color.SECONDARY_DARK}
                  loading={isShipmentTrackingFetching}
                  disabled={disabledToFetchAndEditBookingOrBillOfLandingDetails}
                  style={{ width: "100%" }}
                >
                  {translate("fetchDetails")}
                </Button>
              )}
            </Grid>
          </Grid>
          <span
            style={{
              padding: "8px 8px 8px 14px",
            }}
          >
            {/* <Text hasErrors={fieldHasError("trackingAvailable")}>
              {fieldHasError("trackingAvailable") ? translate("pleaseFillInAtLeastOnField") : null}{" "}
              {translate("trackYourShipmentByFillingInEitherOrBothFields")}
            </Text> */}
          </span>
          {renderTrackingStatusBanner()}
        </Grid>
      )}
      <Grid item container spacing={3} direction="row">
        <Grid item xs={12} sm={3}>
          {isOnView ? (
            <ViewField label={translate("hsCommodityCode")} value={shipment?.commodity} />
          ) : (
            <AutoSuggest
              label={translate("hsCommodityCode")}
              placeholder={translate("selectAnOption")}
              disabled={hasMarineTraffic}
              options={commodities}
              getOptionLabel={(option: Commodity) => option.label}
              getOptionSelected={(option: Commodity, value: Commodity) =>
                option.value === value.value
              }
              value={onGetCommodityInputValue()}
              multiple
              onChange={(options: Option[]) => {
                if (options?.length) {
                  const optionsString = options.map((opt) => opt.value).join("/");

                  setShipment({ ...shipment, commodity: optionsString });
                } else {
                  setShipment({ ...shipment, commodity: undefined });
                }
              }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={3}>
          {isOnView ? (
            <ViewField
              label={translate("carrier")}
              value={renderCarrierViewField(shipment?.carrierScac)}
            />
          ) : (
            <AutoSuggest
              label={translate("carrier")}
              name="carrier"
              // required
              disabled={isShipmentTrackingFetching || hasMarineTraffic || isShipmentTrackingFetched}
              placeholder={translate("select")}
              inputValue={
                renderCarrierValue
                  ? `${renderCarrierValue?.name} - ${renderCarrierValue?.scac}`
                  : ""
              }
              value={renderCarrierValue || ""}
              getOptionLabel={(carrier: Carrier) => {
                return carrier ? `${carrier.name ?? ""} - ${carrier.scac ?? ""}` : "";
              }}
              getOptionSelected={(option: Carrier, value: Carrier) => option.id === value.id}
              onChange={(carrier: Carrier) => {
                setShipment({ ...shipment, carrierScac: carrier?.scac ?? "" });
                setShowInfoCard(!!carrier);
              }}
              options={carrierList}
              // error={fieldHasError("carrierScac")}
              // errorMessage={getErrorByField("carrierScac")}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={3}>
          {isOnView ? (
            <ViewField label={translate("vessel")} value={shipment?.vesselName} />
          ) : (
            <Input
              label={translate("vessel")}
              placeholder={translate("vessel")}
              disabled={isShipmentTrackingFetching || hasMarineTraffic || isShipmentTrackingFetched}
              value={shipment?.vesselName ?? ""}
              onChange={(event) => {
                setShipment({ ...shipment, vesselName: event.target.value });
              }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={3}>
          {isOnView ? (
            <ViewField label={`${translate("voyage")} #`} value={shipment?.voyageNumber} />
          ) : (
            <Input
              label={`${translate("voyage")} #`}
              disabled={isShipmentTrackingFetching || hasMarineTraffic || isShipmentTrackingFetched}
              placeholder={`${translate("voyage")} #`}
              value={shipment?.voyageNumber ?? ""}
              onChange={(event) => {
                setShipment({ ...shipment, voyageNumber: event.target.value });
              }}
            />
          )}
        </Grid>
      </Grid>

      <Grid container item spacing={3} rowGap={3} direction="row">
        <Grid item xs={12} sm={3}>
          {isOnView ? (
            <ViewField
              label={translate("portOfOrigin")}
              value={shipment?.shipmentTracking?.originPort}
            />
          ) : (
            <Input
              label={translate("portOfOrigin")}
              placeholder={translate("portOfOrigin")}
              disabled={isShipmentTrackingFetching || hasMarineTraffic || isShipmentTrackingFetched}
              value={shipment?.shipmentTracking?.originPort ?? ""}
              onChange={(event) => {
                setShipment({
                  ...shipment,
                  shipmentTracking: {
                    ...shipment?.shipmentTracking,
                    originPort: event.target.value,
                  },
                });
              }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={3}>
          {isOnView ? (
            <ViewField
              label={translate("portOfDestination")}
              value={shipment?.shipmentTracking?.finalPort}
            />
          ) : (
            <Input
              label={translate("portOfDestination")}
              placeholder={translate("portOfDestination")}
              disabled={isShipmentTrackingFetching || hasMarineTraffic || isShipmentTrackingFetched}
              value={shipment?.shipmentTracking?.finalPort ?? ""}
              onChange={(event) => {
                setShipment({
                  ...shipment,
                  shipmentTracking: {
                    ...shipment?.shipmentTracking,
                    finalPort: event.target.value,
                  },
                });
              }}
            />
          )}
        </Grid>
      </Grid>
      {showInfoCard && (
        <Grid item container xs={12}>
          <Grid item xs={12} md={8} lg={6}>
            <InfoCard>
              <InfoCardColumn>
                <FiInfo size="20" color="#475467" />
              </InfoCardColumn>
              <InfoCardBody>
                <BodyText>
                  <span>{translate("thisCarrierAllowsShipmentTrackingBy")}:</span>
                </BodyText>
                <div className={styles.options}>
                  <BodyItem>
                    {renderStatusIcon(CarrierTdType.BL)}
                    <span>{translate("billOfLanding")}</span>
                  </BodyItem>
                  <BodyItem>
                    {renderStatusIcon(CarrierTdType.BK)}
                    <span>{translate("booking")}#</span>
                  </BodyItem>
                </div>
              </InfoCardBody>
              <InfoCardColumn>
                <AiOutlineClose
                  size={18}
                  color="#475467"
                  cursor="pointer"
                  onClick={() => setShowInfoCard(false)}
                />
              </InfoCardColumn>
            </InfoCard>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
